<script lang="ts" setup>
export interface MapOptions {
    height: string
    center: MapCoords
    zoom?: number
    fitBounds?: boolean
    clusters?: boolean
}

export interface MapPropsData {
    mapOptions: MapOptions
    markers: MapMarker[]
}

export interface MapProps {
    data: MapPropsData
}

const config = useRuntimeConfig()
const props = defineProps<MapProps>()
const zoomMin = ref<number>(
    props.data.mapOptions.zoom || config.public.gmap.zoomMin
)
const zoomMax = ref<number>(config.public.gmap.zoomMax)
const zoom = ref<number>(zoomMin.value)
const mapCenter = ref<MapCoords>({
    lat: props.data.mapOptions.center.lat,
    lng: props.data.mapOptions.center.lng,
})
const mapRef = ref<any>()
const mapObj = ref<google.maps.Map>()
const isMapReady = ref(false)
const needsCenterOnReady = ref(false)
let centerMapTimeout: ReturnType<typeof setTimeout> | null = null

const { $t } = useNuxtApp()

function render(
    { count, position }: { count: number; position: any },
    stats: any
) {
    if (!google) return
    // change color if this cluster has more markers than the mean cluster
    const color =
        count > Math.max(10, stats.clusters.markers.mean)
            ? '#640003'
            : '#c60108'
    // create svg url with fill color
    const svg = window.btoa(`
  <svg fill="${color}" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240">
    <circle cx="120" cy="120" opacity=".6" r="70" />
    <circle cx="120" cy="120" opacity=".3" r="90" />
    <circle cx="120" cy="120" opacity=".2" r="110" />
  </svg>`)
    // create marker using svg icon
    return new google.maps.Marker({
        position,
        icon: {
            url: `data:image/svg+xml;base64,${svg}`,
            scaledSize: new google.maps.Size(45, 45),
        },
        label: {
            text: String(count),
            color: 'rgba(255,255,255,0.9)',
            fontSize: '12px',
        },
        title: `Skupina ${count} ${usePlural(
            [$t('bod'), $t('bodov'), $t('bodov')],
            count
        )}`,
        // adjust zIndex to be above other markers
        zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
    })
}

const panToMarker = (): void => {
    if (!mapObj.value) return

    const marker = props.data.markers[0]
    if (!marker) return
    const position = new google.maps.LatLng(marker.lat, marker.lng)

    mapObj.value.setZoom(zoomMax.value)
    mapObj.value.panTo(position)

    // Sync zoom ref
    zoom.value = zoomMax.value
}

const fitToBounds = (): void => {
    // is fit bounds disabled?
    if (props.data.mapOptions.fitBounds === false || !google || !mapObj.value) return

    // create bounds from markers
    const bounds = new google.maps.LatLngBounds()
    props.data.markers.forEach((marker: MapMarker) => {
        bounds.extend(new google.maps.LatLng(marker.lat, marker.lng))
    })

    // Don't fit empty bounds
    if (bounds.isEmpty()) return

    // Fit map to bounds
    mapObj.value.fitBounds(bounds)

    // Sync the zoom ref with map's actual zoom after fitBounds completes
    // This prevents Vue from resetting zoom on next render
    google.maps.event.addListenerOnce(mapObj.value, 'idle', () => {
        if (mapObj.value) {
            const newZoom = mapObj.value.getZoom()
            if (newZoom !== undefined) {
                zoom.value = newZoom
            }
        }
    })
}

const centerMap = (): void => {
    if (props.data.markers.length === 0) return

    if (props.data.markers.length === 1) {
        panToMarker()

        return
    }

    fitToBounds()
}

const options = {
    mapTypeControl: false,
    clickableIcons: false,
    styles: [
        {
            featureType: 'poi.business',
            stylers: [{ visibility: 'off' }],
        },
        {
            featureType: 'transit',
            elementType: 'labels.icon',
            stylers: [{ visibility: 'off' }],
        },
    ],
}

// Check if map container is visible (not hidden by tabs)
const isMapVisible = (): boolean => {
    const mapElement = mapRef.value?.$el
    if (!mapElement) return false

    // Check if element or any parent has 'hidden' attribute
    let el: HTMLElement | null = mapElement
    while (el) {
        if (el.hasAttribute('hidden')) return false
        el = el.parentElement
    }
    return true
}

// Force center map with visibility check
const forceCenterMap = (): void => {
    if (!mapObj.value || props.data.markers.length === 0) return

    // Only center if map is visible
    if (!isMapVisible()) return

    const map = mapObj.value

    // Trigger resize and center
    google.maps.event.trigger(map, 'resize')

    setTimeout(() => {
        google.maps.event.trigger(map, 'resize')
        centerMap()
    }, 50)
}

// Debounced center map - prevents multiple rapid calls
const debouncedCenterMap = (): void => {
    if (centerMapTimeout) {
        clearTimeout(centerMapTimeout)
    }
    centerMapTimeout = setTimeout(() => {
        forceCenterMap()
    }, 200)
}

onUpdated(() => {
    // Only center if map is ready, otherwise mark for later
    if (isMapReady.value && mapObj.value) {
        debouncedCenterMap()
    } else {
        needsCenterOnReady.value = true
    }
})

// MutationObserver to detect when tab becomes visible (hidden attribute removed)
let visibilityObserver: MutationObserver | null = null

const setupVisibilityObserver = (): void => {
    const mapElement = mapRef.value?.$el
    if (!mapElement) return

    // Find the tab panel parent with 'hidden' attribute
    let tabPanel: HTMLElement | null = mapElement
    while (tabPanel && !tabPanel.hasAttribute('role')) {
        if (tabPanel.getAttribute('role') === 'tabpanel') break
        tabPanel = tabPanel.parentElement
    }

    if (!tabPanel) return

    visibilityObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' && mutation.attributeName === 'hidden') {
                const target = mutation.target as HTMLElement
                // Tab became visible (hidden attribute removed)
                if (!target.hasAttribute('hidden') && isMapReady.value && mapObj.value) {
                    setTimeout(() => {
                        forceCenterMap()
                    }, 100)
                }
            }
        })
    })

    visibilityObserver.observe(tabPanel, { attributes: true, attributeFilter: ['hidden'] })
}

onMounted(() => {
    mapRef.value.$mapPromise.then((mapObject: google.maps.Map) => {
        // save map object after init
        mapObj.value = mapObject
        isMapReady.value = true

        // Setup observer to detect tab visibility changes
        setupVisibilityObserver()

        // Center map - either because we need to, or on first load
        if (needsCenterOnReady.value || props.data.markers.length > 0) {
            // Delay for initial load to ensure everything is ready
            setTimeout(() => {
                if (isMapVisible()) {
                    centerMap()
                }
            }, 100)
            needsCenterOnReady.value = false
        }
    })
})

// Watch markers - center map when markers array reference changes
watch(
    () => props.data.markers,
    (newMarkers) => {
        if (isMapReady.value && mapObj.value && newMarkers.length > 0) {
            debouncedCenterMap()
        }
    },
    { immediate: true }
)

// Cleanup on unmount
onUnmounted(() => {
    if (centerMapTimeout) {
        clearTimeout(centerMapTimeout)
    }
    if (visibilityObserver) {
        visibilityObserver.disconnect()
    }
})
</script>
<template>
  <div class="position-relative">
    <slot />
    <GMapMap
      :key="mapCenter?.lat + mapCenter?.lng"
      ref="mapRef"
      :center="mapCenter"
      :zoom="zoom"
      :options="options"
      map-type-id="terrain"
      :style="{
        width: '100%',
        height: props.data.mapOptions.height,
      }"
    >
      <GMapCluster
        v-if="props.data.mapOptions.clusters"
        :renderer="{ render }"
      >
        <MapMarkersList :markers="props.data.markers">
          <template #modal="scope: any">
            <slot
              name="modal"
              v-bind="scope"
            />
          </template>
        </MapMarkersList>
      </GMapCluster>
      <MapMarkersList
        v-else
        :markers="props.data.markers"
      >
        <template #modal="scope2: any">
          <slot
            name="modal"
            v-bind="scope2"
          />
        </template>
      </MapMarkersList>
    </GMapMap>
  </div>
</template>
